import React, { useState, useEffect } from "react";

const publicAsset = (path) =>
  `${process.env.PUBLIC_URL || ""}${path.startsWith("/") ? path : `/${path}`}`;

function Services() {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: 1,
      title: "Custom Software Development",
      imageSrc: publicAsset("/assets/img/portfolio/1.jpg"),
      summary: "Web, mobile, and enterprise applications built to last.",
      description:
        "BizMak designs and builds custom web applications, mobile apps, and enterprise platforms — from replacing paper-based processes with automated web systems to full product builds. Our work for Fortune 500 clients like ADT and CGI centers on one outcome: software that reduces errors, cuts turnaround time, and keeps working as your organization grows. Every engagement includes architecture documentation, secure coding practices, and a deployment pipeline your team can operate.",
    },
    {
      id: 2,
      title: "QA Automation & Testing",
      imageSrc: publicAsset("/assets/img/portfolio/6.jpg"),
      summary: "Production-grade test frameworks, not one-off scripts.",
      description:
        "We build maintainable test automation frameworks using Selenium, Playwright, REST Assured, and CI/CD integration with Jenkins and GitHub Actions — plus manual, performance, load, and security testing where it counts. Our QA practice is led by senior SDETs who architect for parallel execution, clean reporting, and reuse, so your test suite stays an asset instead of becoming maintenance debt.",
    },
    {
      id: 3,
      title: "Cloud & DevOps",
      imageSrc: publicAsset("/assets/img/portfolio/3.jpg"),
      summary: "Migration, infrastructure, and pipelines on AWS and beyond.",
      description:
        "From cloud strategy and migration to infrastructure management, containerization, and cost optimization, we help organizations run reliably in the cloud. We build CI/CD pipelines, automate deployments with Docker and infrastructure-as-code, and set up the monitoring and alerting that keeps systems healthy — so releases get faster and outages get rarer.",
    },
    {
      id: 4,
      title: "Cybersecurity & Compliance",
      imageSrc: publicAsset("/assets/img/portfolio/4.jpg"),
      summary: "Threat detection, hardening, and regulatory readiness.",
      description:
        "We protect businesses from cyber threats and data breaches with layered security: firewalls, endpoint protection, patch management, and proactive monitoring that intercepts issues before they escalate. For regulated and government-facing work, we bring IT compliance experience — so your systems meet the standards your contracts require.",
    },
    {
      id: 5,
      title: "Data & Business Intelligence",
      imageSrc: publicAsset("/assets/img/portfolio/2.jpg"),
      summary: "From raw data to decisions — pipelines, dashboards, insight.",
      description:
        "We design and administer databases, build data pipelines, and deliver business intelligence dashboards that turn operational data into decisions. Services include data backup and recovery, database management and administration, and analytics solutions that give leadership real visibility into the numbers that matter.",
    },
    {
      id: 6,
      title: "Managed IT Services",
      imageSrc: publicAsset("/assets/img/portfolio/5.jpg"),
      summary: "Your IT department, without the overhead.",
      description:
        "For organizations that don't need in-house IT staff, BizMak provides a complete managed service: remote monitoring and management, network setup and troubleshooting, server administration, hardware installation, IT procurement, and helpdesk support — on flexible plans with competitive pricing that scale with your business.",
    },
  ];

  const closeModal = () => setSelectedService(null);

  useEffect(() => {
    if (!selectedService) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [selectedService]);

  return (
    <section className="bizmak-section fedsupplier" id="service">
      <div className="container">
        <h2 className="page-section-heading text-center text-blue mb-0">
          Services
        </h2>
        <p className="section-intro">
          End-to-end technology delivery — engineering, cloud, quality,
          security, data, and operations — from a single accountable partner.
        </p>
        <div className="divider-custom">
          <div className="divider-custom-line" />
          <div className="divider-custom-icon">
            <i className="fas fa-star" aria-hidden="true" />
          </div>
          <div className="divider-custom-line" />
        </div>
        <div className="row g-4 justify-content-center">
          {services.map((service) => (
            <div key={service.id} className="col-md-6 col-lg-4">
              <article
                className="bizmak-service-card"
                onClick={() => setSelectedService(service)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedService(service);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Open details: ${service.title}`}
              >
                <div className="ratio ratio-4x3">
                  <img
                    className="img-fluid"
                    src={service.imageSrc}
                    alt=""
                    loading="lazy"
                  />
                </div>
                <div className="bizmak-service-card-body">
                  <h3>{service.title}</h3>
                  <p className="hint mb-0">{service.summary}</p>
                </div>
              </article>
            </div>
          ))}
        </div>

        {selectedService && (
          <div
            className="modal fade show d-block bizmak-modal"
            style={{ backgroundColor: "rgba(11, 18, 32, 0.65)" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-modal-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body text-center pb-5 px-4">
                  <h2
                    id="service-modal-title"
                    className="portfolio-modal-title mb-3"
                  >
                    {selectedService.title}
                  </h2>
                  <div className="divider-custom mb-4">
                    <div className="divider-custom-line" />
                    <div className="divider-custom-icon">
                      <i className="fas fa-star" aria-hidden="true" />
                    </div>
                    <div className="divider-custom-line" />
                  </div>
                  <img
                    className="img-in-modal rounded mb-4"
                    src={selectedService.imageSrc}
                    alt=""
                  />
                  <p className="mb-4 text-start mx-auto" style={{ maxWidth: "40rem" }}>
                    {selectedService.description}
                  </p>
                  <div className="d-flex gap-3 justify-content-center">
                    <a
                      className="btn btn-primary btn-lg px-4"
                      href="#contact"
                      onClick={closeModal}
                    >
                      Discuss your project
                    </a>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg px-4"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Services;
